import * as Sentry from "@sentry/react";
import { OUTLINE_TYPES } from "../../../../../../src/shared/storyEngineConstants";
import { AppEventPublisher, StoryBibleType } from "../../../../AppEventPublisher";
import Auth from "../../../../Auth";
import { CrossAppSignal, useCrossAppSignalStore } from "../../../../stores/crossAppSignalStore";
import { useSeriesStore } from "../../../../stores/seriesStore";
import { useStickyTourStore } from "../../../../stores/stickyTourStore";
import { useStoryBibleStore } from "../../../../stores/storyBibleStore";
import { TourIds, TourSignal } from "../../../StickyTour/TourTypes";
import { useToast } from "../../../ToastWrapper";
import { useStructuredOutlineStore } from "../StructuredOutlineStore";
import { useSudowriteStore } from "../../../../stores/sudowriteStore";
import { useSudoLayoutEventEmitter } from "src/lib/layout-events";
import { usePersistentChatStore } from "src/stores/persistentChatStore";
import { isStoryBibleCtaBlockingRetrigger } from "src/components/PersistentChat/storyBibleCta";

interface UseGenerateOutlineParams {
  projectId: string;
  onGenerate?: () => void;
}

interface HandleGenerateOptions {
  withToast?: boolean;
}

export function useGenerateOutline({ projectId, onGenerate }: UseGenerateOutlineParams) {
  const isAgentGeneratingOutline = useCrossAppSignalStore((state) => state.signals[CrossAppSignal.AGENTIC_OUTLINE_RUNNING]);
  const hasAccessToAgenticChat = useSudowriteStore((state: any) => state.subscriptionJSON?.hasAccessToAgenticChat);
  const emitLayoutEvent = useSudoLayoutEventEmitter();
  const globalCtaActive = usePersistentChatStore((state: any) =>
    isStoryBibleCtaBlockingRetrigger(state.ctaAction),
  );
  const selectedType = useStructuredOutlineStore((state) => state.outline.content.generationFramework) || OUTLINE_TYPES[0].value;
  const selectedOutline = OUTLINE_TYPES.find(({ value }) => value === selectedType) || OUTLINE_TYPES[0];
  const scenes = useStoryBibleStore((state) => state.scenes);
  const outline = useStructuredOutlineStore((state) => state.outline);
  const setOutline = useStructuredOutlineStore((state) => state.setOutline);
  const readOnly = useStructuredOutlineStore((state) => state.readOnly);
  const setReadOnly = useStructuredOutlineStore((state) => state.setReadOnly);
  const customOutlineInstructions = useStructuredOutlineStore((state) => state.outline.content.instructions);
  const selectedProjects = useSeriesStore((state) => state.selectedProjects);
  const projectToUpdate = selectedProjects.outline?.projectId || projectId;
  const { setToast, dismissToast } = useToast();
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const sendFailureSignal = useStickyTourStore((state) => state.sendFailureSignal);
  const activeTourId = useStickyTourStore((state) => state.activeTourId);
  const currentStepIndex = useStickyTourStore((state) => state.currentStepIndex);
  const tourSteps = useStickyTourStore((state) => state.steps);

  const currentStepId = tourSteps[currentStepIndex]?.id;
  const isGenerateDisabled = activeTourId === TourIds.STORY_BIBLE && currentStepId === "7";


  const handleGenerate = async ({ withToast = false }: HandleGenerateOptions = {}) => {
    if (isGenerateDisabled || !selectedOutline || isAgentGeneratingOutline || readOnly) return;
    if (hasAccessToAgenticChat) {
      if (globalCtaActive) return;
      sendFlowSignal(TourSignal.STORY_BIBLE_OUTLINE_GENERATE_CLICKED);
      emitLayoutEvent("trigger_cta_chat", {
        type: "generate_outline",
        targetName: selectedOutline.label,
        outlineFramework: selectedOutline.value,
        customInstructions: customOutlineInstructions || undefined,
        displayTitle: "Generate Outline",
        userMessage: (() => {
          const name = selectedOutline.label;
          const lower = name.toLowerCase();
          const hasOutline = lower.includes("outline");
          const startsWithThe = lower.startsWith("the ");
          const prefix = startsWithThe ? "" : "a ";
          return hasOutline ? `Generate ${prefix}${lower}` : `Generate ${prefix}${lower} outline`;
        })(),
        cannedResponse: "Ok, let's do it!",
      });
      return;
    }
    sendFlowSignal(TourSignal.STORY_BIBLE_OUTLINE_GENERATE_CLICKED);
    setReadOnly(true);
    if (withToast) {
      setToast("Generating outline…", { durationMs: Infinity });
    }
    const body = {
      outline,
      scenes,
      projectId: projectToUpdate,
      outlineFramework: selectedOutline.value,
      customInstructions: customOutlineInstructions || "",
    };
    try {
      AppEventPublisher.storyBible.generate(StoryBibleType.outline);
      const result = await Auth.fetchWithIdToken("/api/ai/storyEngine/outline/generate", body);
      if (result.error) {
        setToast(result.message || "There was an error generating the outline. Please try again.");
        sendFailureSignal(TourSignal.STORY_BIBLE_OUTLINE_GENERATE_FAILED);
      } else {
        setReadOnly(false);
        if (withToast) {
          dismissToast();
        }
        sendFlowSignal(TourSignal.STORY_BIBLE_OUTLINE_GENERATED);
        setTimeout(() => {
          setOutline(result.outline);
          onGenerate?.();
        }, 200);
      }
    } catch (err) {
      Sentry.captureException(err, {
        tags: {
          feature: "structured_outline_generate_outline",
        },
      });
      setToast("There was an error generating the outline. Please try again.");
      sendFailureSignal(TourSignal.STORY_BIBLE_OUTLINE_GENERATE_FAILED);
    } finally {
      setReadOnly(false);
    }
  };

  return {
    handleGenerate,
    isGenerateDisabled,
    isAgentGeneratingOutline,
    selectedOutline,
    selectedType,
  };
}
